<template>
  <v-container>
    <v-card
      outlined
      class="pa-3 mb-3"
    >
      <v-row dense>
        <v-col
          cols="6"
          md="2"
        >
          <v-text-field
            v-model="dateFrom"
            type="date"
            outlined
            dense
            hide-details
            label="ແຕ່ວັນທີ"
          />
        </v-col>
        <v-col
          cols="6"
          md="2"
        >
          <v-text-field
            v-model="dateTo"
            type="date"
            outlined
            dense
            hide-details
            label="ຫາວັນທີ"
          />
        </v-col>
        <v-col
          cols="6"
          md="2"
        >
          <v-select
            v-model="status"
            :items="statuses"
            outlined
            dense
            hide-details
            label="ຜົນ"
          />
        </v-col>
        <v-col
          cols="6"
          md="4"
        >
          <v-text-field
            v-model="filter"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            :label="searchLabel"
            @keyup.enter="load"
          />
        </v-col>
        <v-col
          cols="12"
          md="2"
          class="d-flex align-center"
        >
          <!-- The dates and filters apply only when this is pressed. -->
          <v-btn
            color="primary"
            depressed
            block
            :loading="loading"
            @click="load"
          >
            <v-icon left>
              mdi-magnify
            </v-icon>
            ຄົ້ນຫາ
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <div class="mb-2 text-body-2">
      {{ doneWord }} {{ count('approved') }} ລາຍ · ຖືກປະຕິເສດ {{ count('rejected') }} ລາຍ
    </div>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :items-per-page="50"
        no-data-text="ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້"
      >
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            x-small
            :color="item.status === 'approved' ? 'red lighten-5' : 'grey lighten-3'"
            :text-color="item.status === 'approved' ? 'red darken-2' : 'grey darken-2'"
          >
            {{ item.status === 'approved' ? doneWord : `ປະຕິເສດ (ຂັ້ນ ${item.rejected_step})` }}
          </v-chip>
        </template>
        <template v-slot:[`item.customer_name`]="{ item }">
          <div>{{ item.customer_name }}</div>
          <div class="text-caption grey--text">
            {{ item.customer_code }} · {{ item.customer_phone }}
          </div>
        </template>
        <template v-slot:[`item.bill_number`]="{ item }">
          <div>{{ item.bill_number }}</div>
          <div class="text-caption grey--text">
            {{ item.content }} · {{ billMonth(item) }}
          </div>
        </template>
        <template v-slot:[`item.total`]="{ item }">
          {{ money(item.total) }}
        </template>
        <template v-slot:[`item.to_status`]="{ item }">
          {{ statusChange(item) }}
        </template>
        <template v-slot:[`item.reason`]="{ item }">
          <div style="min-width: 160px">
            {{ item.reason }}
          </div>
          <div
            v-if="item.reject_reason"
            class="text-caption red--text"
          >
            ປະຕິເສດ: {{ item.reject_reason }}
          </div>
        </template>
        <template v-slot:[`item.trail`]="{ item }">
          <div class="text-caption">
            ສົ່ງ: {{ item.submitter }} · {{ item.requested_at }}
          </div>
          <div
            v-for="s in [1, 2, 3]"
            :key="s"
            class="text-caption"
          >
            <template v-if="item[`approver${s}`]">
              ຂັ້ນ {{ s }}: {{ item[`approver${s}`] }}
            </template>
          </div>
          <div
            v-if="item.rejecter"
            class="text-caption red--text"
          >
            ປະຕິເສດໂດຍ: {{ item.rejecter }}
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { today } from '@/Helpers/localDate';
import { money, statusChange, billMonth } from './cells';

/** History of a household three-step request: applied and rejected, filtered by the finishing day. */
export default {
  name: 'ThreeStepHistory',
  props: {
    api: { type: String, required: true },
    columns: { type: Array, required: true },
    doneWord: { type: String, default: 'ລຶບແລ້ວ' },
    searchLabel: { type: String, default: 'ຊື່, ເບີໂທ, CUS_' },
  },
  data() {
    return {
      // Opens on today, like the other history pages.
      dateFrom: today(),
      dateTo: today(),
      status: 'all',
      filter: '',
      rows: [],
      loading: false,
    };
  },
  computed: {
    statuses() {
      // Vuetify does not show a selected null value, so "all" is a real value.
      return [
        { text: 'ທັງໝົດ', value: 'all' },
        { text: this.doneWord, value: 'approved' },
        { text: 'ຖືກປະຕິເສດ', value: 'rejected' },
      ];
    },
    headers() {
      return [
        { text: 'ລຳດັບ', value: 'no' },
        { text: 'ວັນທີ', value: 'finished_at' },
        { text: 'ຜົນ', value: 'status' },
        ...this.columns,
        { text: 'ເຫດຜົນ', value: 'reason', sortable: false },
        { text: 'ຜູ້ສົ່ງ / ຜູ້ອະນຸມັດ', value: 'trail', sortable: false },
        { text: 'ເລກຄຳຂໍ', value: 'id' },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    money,
    statusChange,
    billMonth,
    count(status) {
      return this.rows.filter((r) => r.status === status).length;
    },
    load() {
      this.loading = true;
      this.$axios
        .get(`${this.api}/history`, {
          params: {
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
            status: this.status === 'all' ? undefined : this.status,
            filter: this.filter || undefined,
          },
        })
        .then((res) => {
          this.rows = res.data.data.data || [];
        })
        .catch((error) => this.$store.commit('Toast_State', {
          value: true,
          color: 'error',
          msg: (error.response && error.response.data && error.response.data.message) || 'Something went wrong',
        }))
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
